import { useReducer } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, Flex, Text, Image } from "theme-ui";
import { Settings, User, MessageSquare } from "react-feather";
import { useTranslation } from "react-i18next";

import { Accordion, Carousel, IconMenuOption } from "src/components";
import { ActionKind, State, Action } from "./AccordionTabs.d";
import vec_sm from "src/assets/images/icon_reduced.svg";

const tabReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionKind.SelectTab:
      return {
        selectedTab: action.payload,
        openedAccordion:
          state.selectedTab === action.payload ? state.openedAccordion : null,
      };
    case ActionKind.selectAccordion:
      return {
        ...state,
        openedAccordion:
          state.openedAccordion === action.payload ? null : action.payload,
      };
    default:
      return state;
  }
};

export const AccordionTabs: React.FC = () => {
  const { t } = useTranslation();

  const valuesAccordion: {
    content: { subText: string; subTitle: string }[];
    section: string;
    text: string;
  }[] = t("faq.sections", { returnObjects: true });

  const tabs = [
    {
      option: valuesAccordion[0].section,
      text: valuesAccordion[0].text,
      icon: (active?: boolean) => (
        <Image
          src={vec_sm}
          width="34px"
          style={{ opacity: active ? 1 : 0.5 }}
        />
      ),
      content: valuesAccordion[0].content,
    },
    {
      option: valuesAccordion[1].section,
      text: valuesAccordion[1].text,
      icon: () => <Settings />,
      colors: ["tertiary", "accent"],
      content: valuesAccordion[1].content,
    },
    {
      option: valuesAccordion[2].section,
      text: valuesAccordion[2].text,
      icon: () => <User />,
      colors: ["accent", "secondary"],
      content: valuesAccordion[2].content,
    },
    {
      option: valuesAccordion[3].section,
      text: valuesAccordion[3].text,
      icon: () => <MessageSquare />,
      colors: ["secondary", "highlight"],
      content: valuesAccordion[3].content,
    },
  ];

  const [state, dispatch] = useReducer(tabReducer, {
    selectedTab: 0,
    openedAccordion: null,
  });

  const selectTab = (index: number) => {
    dispatch({ type: ActionKind.SelectTab, payload: index });
  };

  const selectAccordion = (index: number) => {
    dispatch({ type: ActionKind.selectAccordion, payload: index });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Carousel
        data={tabs}
        renderItem={(item, index) => (
          <IconMenuOption
            key={index}
            active={state.selectedTab === index}
            title={item.option}
            customIcon={item.icon(state.selectedTab === index)}
            colors={item.colors}
            onClick={() => selectTab(index)}
          />
        )}
      />

      <main>
        <AnimatePresence exitBeforeEnter>
          <motion.div
            key={state.selectedTab ? state.selectedTab : "0001"}
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 20 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.15 }}
          >
            <Box
              as="article"
              sx={{
                textAlign: "center",
                mt: 5,
                pt: [1, 1, 2],
                px: [2, 8, 10],
                mb: 6,
              }}
            >
              <Text
                as="h3"
                sx={{
                  fontSize: [4, 5, 6],
                  fontFamily: "heading",
                  fontWeight: 800,
                  display: "block",
                  mb: 2,
                }}
              >
                {tabs[state.selectedTab].option}
              </Text>
              <Text
                as="p"
                sx={{
                  fontSize: [2, 2, 3],
                }}
              >
                {tabs[state.selectedTab].text}
              </Text>
            </Box>
            <Flex sx={{ flexDirection: "column", px: [0, 0, 7] }}>
              {tabs[state.selectedTab].content.map((item, index) => {
                return (
                  <Accordion
                    key={index}
                    title={item.subTitle}
                    content={item.subText}
                    isOpen={state.openedAccordion === index}
                    onAccordionClick={() => selectAccordion(index)}
                    mb="10px"
                  />
                );
              })}
            </Flex>
          </motion.div>
        </AnimatePresence>
      </main>
    </Box>
  );
};
